import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { WebsocketClient } from 'src/app/common/service/websocket-client.service';
import { ChatMessage } from '../model/chat-message';
import { StoreService } from 'src/app/store/service/store.service';
import { WEBSOCKET_TOPIC_MESSAGES, WEBSOCKET_TOPIC_MESSAGES_UNREAD } from 'src/app/common/const';
import { HttpClient } from '@angular/common/http';
import { UserProfileService } from 'src/app/common/service/user-profile.service';
import { map, takeUntil, take } from 'rxjs/operators';
import { plainToClass } from 'class-transformer';
import { RestService } from 'src/app/shared/service/rest.service';
import { UnreadMap } from '../model/unread-map';
import { isArray } from 'lodash';
import { Receiver } from '../model/receiver';
import { ChannelService } from './channel.service';
import { PredefinedMessage } from '../model/predefined-message';

@Injectable({
  providedIn: 'root'
})
export class ChatService extends RestService {

  private username: string;

  private unsubscribe$: Subject<void> = new Subject();
  private visibleSub = new Subject();
  visibiltyStream$ = this.visibleSub.asObservable();

  constructor(private websocket: WebsocketClient, private storeService: StoreService, http: HttpClient,
    private userProfileService: UserProfileService, private channelService: ChannelService) {
    super(http);
    this.username = this.userProfileService.getUserName();
  }

  get isConnected(): boolean {
    return this.websocket.isConnected();
  }

  join(channel: string, receivers: Receiver[]): void {
    this.channelService.setActualChannel(channel);
    this.websocket.websocketConnectionConnected$.pipe(take(1)).subscribe({
      next: () => {
        this.clearAllUnread(channel);
        this.websocket
          .send(`${WEBSOCKET_TOPIC_MESSAGES}/${this.username}/join`, Object.assign(new ChatMessage(), { channel, username: this.username, receivers }));
      }
    });
  }

  add(message: ChatMessage): void {
    message.username = this.username;
    this.websocket.send(`${WEBSOCKET_TOPIC_MESSAGES}/${this.username}/add`, message);
  }

  markReaded(message: ChatMessage): void {
    this.websocket.send(`${WEBSOCKET_TOPIC_MESSAGES}/${this.username}/confirm`, message);
  }

  savePredefinedMessage(message: PredefinedMessage): Observable<PredefinedMessage[]> {
    const api: string = message.id ? 'api/user/message/update' : 'api/user/message/add';
    return this.postAndExtract(PredefinedMessage, api, { data: message });
  }

  fetchPredefinedMessages(): Observable<PredefinedMessage[]> {
    return this.postAndExtract(PredefinedMessage, 'api/user/message/fetch');
  }

  unsubscribeData(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  getUsername(): string {
    return this.username;
  }

  init(): void {
    this.websocket.websocketConnectionConnected$.pipe(take(1)).subscribe(() => {
      this.getUnreadSubscription();
      this.getDataSubscription();
    });
  }

  deletePredefinedMessage(message: PredefinedMessage): Observable<PredefinedMessage> {
    return this.doPost('api/user/message/remove', { data: message });
  }

  setVisibility(isVisible: boolean): void {
    this.visibleSub.next(isVisible);
  }

  private getDataSubscription(): void {

    this.websocket.subscribe(`${WEBSOCKET_TOPIC_MESSAGES}/${this.username}`)
      .pipe(takeUntil(this.unsubscribe$), map((data: Record<string, any> | Record<string, any>[]) => {
        const plainedData = plainToClass(ChatMessage, data);
        return isArray(plainedData) ? plainedData : [plainedData];
      }))
      .subscribe(messages => this.storeService.store(ChatMessage.getName(), messages));
  }

  private getUnreadSubscription(): void {
    this.websocket.subscribe(`${WEBSOCKET_TOPIC_MESSAGES_UNREAD}/${this.username}`)
      .pipe(takeUntil(this.unsubscribe$), map(unread => unread as UnreadMap))
      .subscribe(data => this.storeService.store(this.channelService.unreadStoreName, Object.entries(data).map(([channel, unread]) => ({ channel, unread }))));
  }

  private clearAllUnread(channel: string): void {
    this.websocket.send(`${WEBSOCKET_TOPIC_MESSAGES_UNREAD}/${this.username}/clear`, Object.assign(new ChatMessage(), {
      channel
    }));
  }
}

